(() => {
  window.dacSavedBrowsingConnect = ({ filters, sortSelect, apply }) => {
    const panel = document.querySelector('[data-dac-saved-browsing]');
    if (!panel || !sortSelect || !filters) return null;
    const base = panel.dataset.collectionUrl.replace(/\/$/, '');
    const defaultSort = panel.dataset.defaultSort;
    const sortOnly = panel.dataset.sortOnly === 'true';
    const key = 'dac:browsing:v1:' + panel.dataset.scope + ':' + base;
    const toggles = [...panel.querySelectorAll('[data-saved-setting]')];
    const restore = panel.querySelector('[data-saved-apply]');
    const reset = panel.querySelector('[data-saved-reset]');
    const title = panel.querySelector('[data-saved-title]');
    const sortDetail = panel.querySelector('[data-saved-sort-detail]');
    const filterDetail = panel.querySelector('[data-saved-filter-detail]');
    const note = panel.querySelector('[data-saved-note]');
    const trigger = panel.querySelector('[data-saved-trigger]');
    const badge = panel.querySelector('[data-saved-label]');
    function closeMenu() {
      panel.open = false;
      trigger?.focus({ preventScroll: true });
    }
    panel.addEventListener('keydown', event => {
      if (event.key === 'Escape' && panel.open) { event.preventDefault(); event.stopPropagation(); closeMenu(); }
    });
    panel.addEventListener('focusout', event => {
      if (event.relatedTarget && !panel.contains(event.relatedTarget)) panel.open = false;
    });
    document.addEventListener('click', event => {
      if (panel.open && !panel.contains(event.target)) panel.open = false;
    });
    const controls = [...filters.querySelectorAll('input[data-query-filter="true"]')];
    const tagControls = [...filters.querySelectorAll('input[type="checkbox"]:not([data-query-filter="true"])')];
    const vendor = document.getElementById('archive-artist-select');
    const sorts = new Set([...sortSelect.options].map(option => option.value));
    sorts.add(defaultSort);
    const allowed = new Set(controls.map(input => input.name));
    if (vendor) allowed.add('filter.p.vendor');
    let available = true, saved = null, busy = false, failed = false, trimmed = false;
    let current;
    function snapshot(url) {
      const target = new URL(url, location.origin);
      const tags = target.pathname.startsWith(base + '/') ? target.pathname.slice(base.length + 1).split('+').filter(Boolean).map(decodeURIComponent) : [];
      const params = [...target.searchParams].filter(([name]) => name === 'sort_by' || allowed.has(name));
      if (!params.some(([name]) => name === 'sort_by')) params.push(['sort_by', defaultSort]);
      return { version: 1, tags, params };
    }
    function identity(value) {
      return JSON.stringify({ tags: [...value.tags].sort(), params: value.params.map(pair => pair.join('\u0000')).sort() });
    }
    function selected(value, remember) {
      return { version: 2, remember, tags: remember.filters ? value.tags : [], params: value.params.filter(([name]) => name === 'sort_by' ? remember.sort : remember.filters) };
    }
    function combined(value) {
      const other = selected(current, { sort: !value.remember.sort, filters: !value.remember.filters });
      return { tags: [...value.tags, ...other.tags], params: [...value.params, ...other.params] };
    }
    function sanitize(value) {
      if (!value || ![1, 2].includes(value.version) || !Array.isArray(value.tags) || !Array.isArray(value.params) || value.tags.length > 50 || value.params.length > 100) return null;
      if (value.version === 2 && (!value.remember || typeof value.remember.sort !== 'boolean' || typeof value.remember.filters !== 'boolean')) return null;
      const remember = value.version === 1 ? { sort: true, filters: !sortOnly } : { sort: value.remember.sort, filters: !sortOnly && value.remember.filters };
      if (!remember.sort && !remember.filters) return null;
      if (!value.tags.every(tag => typeof tag === 'string' && tag.length < 200) || !value.params.every(pair => Array.isArray(pair) && pair.length === 2 && pair.every(item => typeof item === 'string' && item.length < 500))) return null;
      const tags = [...new Set(value.tags)].filter(tag => tagControls.some(input => input.value === tag));
      const params = value.params.filter(([name, val]) => {
        if (name === 'sort_by') return sorts.has(val);
        if (name === 'filter.p.vendor' && vendor) return [...vendor.options].some(option => option.value === val);
        return controls.some(input => input.name === name && (input.type === 'number' ? val.trim() !== '' && Number.isFinite(Number(val)) && Number(val) >= 0 : input.value === val));
      });
      if (remember.sort && !params.some(([name]) => name === 'sort_by')) params.push(['sort_by', defaultSort]);
      const clean = selected({ tags, params: [...new Map(params.map(pair => [pair.join('\u0000'), pair])).values()] }, remember);
      trimmed = identity(clean) !== identity(selected(value, remember));
      return clean;
    }
    function urlFor(value) {
      const url = new URL(location.href);
      url.pathname = base + (value.tags.length ? '/' + value.tags.map(encodeURIComponent).join('+') : '');
      for (const name of [...url.searchParams.keys()]) {
        if (name === 'sort_by' || name.startsWith('filter.') || /^dac_(?:unpurchased|colors100|sale|ai_exclude|wishlist|exclusives|name|snapshot)$/.test(name) || name === 'page' || name === 'section_id') url.searchParams.delete(name);
      }
      value.params.forEach(([name, val]) => url.searchParams.append(name, val));
      return url.href;
    }
    function write(value) {
      try {
        if (value) localStorage.setItem(key, JSON.stringify(value));
        else localStorage.removeItem(key);
        saved = value;
        return true;
      } catch (_) { available = false; return false; }
    }
    function summary(value) {
      function labelText(input) {
        const label = input?.closest('label')?.cloneNode(true);
        label?.querySelectorAll('[aria-hidden="true"]').forEach(node => node.remove());
        return label?.textContent.trim().replace(/\s+/g, ' ');
      }
      const sort = value.params.find(([name]) => name === 'sort_by')?.[1] || defaultSort;
      const sortLabel = [...sortSelect.options].find(option => option.value === sort)?.textContent.trim() || sort;
      const result = [];
      for (const tag of value.tags) {
        const input = tagControls.find(input => input.value === tag);
        result.push(labelText(input) || tag);
      }
      for (const [name, val] of value.params) {
        if (name === 'sort_by') continue;
        if (name === 'filter.p.vendor' && vendor) result.push([...vendor.options].find(option => option.value === val)?.textContent.trim() || val);
        else {
          const input = controls.find(input => input.name === name && (input.type === 'number' || input.value === val));
          const text = labelText(input);
          result.push(input?.type === 'number' ? (name.endsWith('.gte') ? 'Min price: ' : 'Max price: ') + val + ' ' + panel.dataset.currency : text || val);
        }
      }
      return { sort: sortLabel, filters: result.length ? result.length + (result.length === 1 ? ' filter selected' : ' filters selected') : 'No filters', full: result.join(' · ') || 'No filters' };
    }
    function render() {
      const matching = saved && identity(saved) === identity(selected(current, saved.remember));
      const prefs = saved?.remember || { sort: false, filters: false };
      const choice = summary(saved ? combined(saved) : current);
      panel.dataset.saved = saved ? 'true' : 'false';
      toggles.forEach(toggle => {
        toggle.setAttribute('aria-checked', String(prefs[toggle.dataset.savedSetting]));
        toggle.disabled = !available || busy;
      });
      sortDetail.textContent = choice.sort;
      if (filterDetail) { filterDetail.textContent = choice.filters; filterDetail.title = choice.full; }
      restore.disabled = busy;
      reset.disabled = busy;
      restore.hidden = !saved || !!matching || !available;
      reset.hidden = !saved && identity(current) === identity({ tags: [], params: [['sort_by', defaultSort]] });
      const activeTitle = prefs.sort && prefs.filters ? "Filtres et tri enregistrés actifs" : prefs.sort ? "Tri enregistré actif" : "Filtres enregistrés actifs";
      title.textContent = !available ? "L’enregistrement est indisponible dans ce navigateur" : failed && saved ? "Vos choix enregistrés n’ont pas pu être appliqués" : busy && saved ? "Mise à jour de votre affichage enregistré…" : saved && matching ? activeTitle : saved ? "Choix enregistrés suspendus pour cet affichage" : (sortOnly ? "Mémoriser le tri" : "Mémoriser les filtres et le tri");
      note.textContent = !available ? "Le stockage du navigateur est indisponible. Vous pouvez continuer à naviguer normalement." : failed ? "Impossible d’appliquer les choix. Réessayez ci-dessous." : saved && !matching && !busy ? "Cet affichage diffère de vos choix enregistrés." : trimmed ? "Les choix indisponibles ont été ignorés." : '';
      note.hidden = !note.textContent;
      if (badge && trigger) {
        badge.textContent = !available ? "Indisponible" : failed && saved ? "Enregistré · réessayer" : busy && saved ? "Enregistré · …" : saved && matching ? (prefs.sort && prefs.filters ? "Les deux sont enregistrés" : prefs.sort ? "Tri enregistré" : "Filtres enregistrés") : saved ? "Enregistré · suspendu" : "Mémoriser";
        trigger.setAttribute('aria-label', badge.textContent + ' — ' + title.textContent);
        trigger.title = saved ? [prefs.sort ? 'Sort: ' + choice.sort : '', prefs.filters ? choice.full : ''].filter(Boolean).join(' · ') : title.textContent;
      }
      panel.hidden = false;
    }
    current = snapshot(location.href);
    function read() {
      const raw = localStorage.getItem(key);
      try { return sanitize(JSON.parse(raw || 'null')); } catch (_) { return null; }
    }
    try { saved = read(); }
    catch (_) { available = false; }
    try {
      const pending = JSON.parse(sessionStorage.getItem(key + ':pending') || 'null');
      if (saved && pending && identity(pending) === identity(current)) write(selected(current, saved.remember));
      sessionStorage.removeItem(key + ':pending');
    } catch (_) {}
    const initial = new URL(location.href);
    const explicit = current.tags.length || [...initial.searchParams.keys()].some(name => name === 'sort_by' || name.startsWith('filter.') || /^dac_(?:unpurchased|colors100|sale|ai_exclude|wishlist|exclusives|name)$/.test(name) || name === 'page');
    const restoreURL = saved && !explicit && identity(saved) !== identity(selected(current, saved.remember)) ? urlFor(combined(saved)) : null;
    toggles.forEach(toggle => toggle.addEventListener('click', () => {
      const kind = toggle.dataset.savedSetting;
      const remember = { sort: !!saved?.remember.sort, filters: !!saved?.remember.filters };
      remember[kind] = !remember[kind];
      const keep = saved ? selected(saved, { sort: remember.sort && kind !== 'sort', filters: remember.filters && kind !== 'filters' }) : { tags: [], params: [] };
      const added = selected(current, { sort: remember.sort && kind === 'sort', filters: remember.filters && kind === 'filters' });
      if (write(remember.sort || remember.filters ? { version: 2, remember, tags: [...keep.tags, ...added.tags], params: [...keep.params, ...added.params] } : null)) { failed = false; trimmed = false; }
      render();
    }));
    restore.addEventListener('click', () => { if (saved) { apply(urlFor(combined(saved))); closeMenu(); } });
    reset.addEventListener('click', () => {
      if (saved && !write(null)) { render(); return; }
      failed = false; trimmed = false;
      apply(urlFor({ tags: [], params: [['sort_by', defaultSort]] }));
      closeMenu();
    });
    window.addEventListener('pageshow', event => {
      if (!event.persisted) return;
      try { saved = read(); } catch (_) { available = false; }
      current = snapshot(location.href); render();
    });
    window.addEventListener('storage', event => {
      if (event.key !== key && event.key !== null) return;
      try { saved = read(); } catch (_) { available = false; }
      render();
    });
    render();
    return {
      restoreURL,
      navigate(url) {
        if (saved) {
          try { sessionStorage.setItem(key + ':pending', JSON.stringify(snapshot(url))); } catch (_) {}
        }
      },
      start() { busy = true; failed = false; render(); },
      complete(url, shouldSave) {
        busy = false; failed = false; current = snapshot(url);
        if (saved && shouldSave) { trimmed = false; write(selected(current, saved.remember)); }
        render();
      },
      error() { busy = false; failed = true; render(); }
    };
  };
})();
